import {
  CheckOutlined,
  ControlOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Button, Empty, Popconfirm, Spin, message } from "antd";
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../firebase";

type TranscriptionJob = {
  id: string;
  dateCreated: number;
  fileName: string;
  isDeleted: boolean;
  type: string;
  url: string;
  isComplete?: boolean;
};

export const Transcribe = () => {
  const [jobList, setJobList] = useState<Array<TranscriptionJob>>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "uploads"),
      (snapshot) => {
        const jobs = snapshot.docs
          .map(
            (snapDoc) =>
              ({ ...snapDoc.data(), id: snapDoc.id }) as TranscriptionJob,
          )
          .sort((a, b) => (b.dateCreated || 0) - (a.dateCreated || 0));

        setJobList(jobs);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  const activeJobs = jobList.filter((job) => !job.isDeleted);
  const pendingJobs = activeJobs.filter((job) => !job.isComplete);
  const completedJobs = activeJobs.filter((job) => job.isComplete);

  const download = (storagePathOrUrl: string) => {
    const downloadPromise = storagePathOrUrl.startsWith("http")
      ? Promise.resolve(storagePathOrUrl)
      : getDownloadURL(ref(storage, storagePathOrUrl));

    downloadPromise
      .then((url) => window.open(url, "_blank"))
      .catch((err) => {
        console.error(err);
        message.error("Failed to open file.");
      });
  };

  return (
    <div className="min-h-screen w-full bg-[#1f1e23] text-white selection:bg-[#F6B34B] selection:text-[#1D1928]">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,_rgba(246,179,75,0.08),_transparent_35%),linear-gradient(to_bottom,_rgba(0,0,0,0.15),_transparent)]" />

      <main className="relative z-10 mx-auto flex w-full max-w-7xl flex-col gap-8 px-6 py-10 md:py-16">
        <header className="border border-white/10 bg-[#1D1928]/50 p-6 backdrop-blur-sm md:p-10">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C6B73] animate-pulse">
                {"// TRANSCRIBE.QUEUE :: AUDIO_JOBS"}
              </p>
              <h1 className="mt-5 font-oswald text-5xl uppercase leading-none tracking-tighter text-[#F6B34B] md:text-7xl">
                Transcription Jobs
              </h1>
              <p className="mt-5 max-w-xl border-l-2 border-[#F6B34B] pl-4 font-mono text-sm leading-relaxed text-gray-300 md:text-base">
                Review uploaded transcription files, download source media, and
                mark completed jobs.
                <span className="mt-2 block text-[#5C6B73]">
                  {pendingJobs.length} pending / {completedJobs.length} complete
                </span>
              </p>
            </div>

            <button
              className="border border-[#5C6B73] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[#5C6B73] transition-all hover:border-[#F6B34B] hover:text-[#F6B34B]"
              onClick={() => nav("/tools")}
              type="button"
            >
              <ControlOutlined /> Tools
            </button>
          </div>
        </header>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="border border-[#9A6D26]/20 bg-black/10 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              Active Jobs
            </p>
            <div className="mt-3 font-oswald text-4xl text-[#F6B34B]">
              {activeJobs.length}
            </div>
          </div>
          <div className="border border-[#9A6D26]/20 bg-black/10 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              Pending
            </p>
            <div className="mt-3 font-oswald text-4xl text-white">
              {pendingJobs.length}
            </div>
          </div>
          <div className="border border-[#9A6D26]/20 bg-black/10 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              Complete
            </p>
            <div className="mt-3 font-oswald text-4xl text-white">
              {completedJobs.length}
            </div>
          </div>
        </section>

        <section className="border border-[#9A6D26]/20 bg-black/10">
          <div className="border-b border-white/10 px-5 py-4 md:px-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              {"// uploads.index"}
            </p>
            <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
              Upload Queue
            </h2>
            <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
          </div>

          <div className="p-5 md:p-8">
            {loading && (
              <div className="flex min-h-[260px] items-center justify-center">
                <Spin size="large" />
              </div>
            )}

            {!loading && activeJobs.length === 0 && (
              <div className="bg-white p-8">
                <Empty description="No transcription jobs found" />
              </div>
            )}

            {!loading && activeJobs.length > 0 && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {activeJobs.map((job) => (
                  <article
                    key={job.id}
                    className={`border p-5 transition-all duration-300 ${
                      job.isComplete
                        ? "border-white/10 bg-white/[0.02]"
                        : "border-[#F6B34B]/40 bg-[#F6B34B]/[0.04]"
                    }`}
                  >
                    <div className="mb-5 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="mb-3 text-2xl text-[#F6B34B]">
                          <FileTextOutlined />
                        </div>
                        <h3 className="break-words font-oswald text-2xl uppercase tracking-tight text-white">
                          {job.fileName || "Untitled Upload"}
                        </h3>
                        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B73]">
                          {job.dateCreated
                            ? new Date(job.dateCreated).toLocaleString()
                            : "No date"}
                        </p>
                      </div>

                      <span
                        className={`shrink-0 border px-3 py-2 font-mono text-[9px] uppercase tracking-widest ${
                          job.isComplete
                            ? "border-green-400/30 text-green-300"
                            : "border-[#F6B34B]/40 text-[#F6B34B]"
                        }`}
                      >
                        {job.isComplete ? "Complete" : "Pending"}
                      </span>
                    </div>

                    <p className="mb-5 break-all font-mono text-[10px] leading-relaxed text-[#5C6B73]">
                      {job.url}
                    </p>

                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => download(job.url)}
                      >
                        Download
                      </Button>

                      {!job.isComplete && (
                        <Popconfirm
                          title="Have you completed this?"
                          onConfirm={() => {
                            updateDoc(doc(db, `uploads/${job.id}`), {
                              isComplete: true,
                            });
                          }}
                        >
                          <Button type="primary" icon={<CheckOutlined />}>
                            Complete
                          </Button>
                        </Popconfirm>
                      )}

                      <Popconfirm
                        title="Are you sure you want to delete this?"
                        onConfirm={() => {
                          updateDoc(doc(db, `uploads/${job.id}`), {
                            isDeleted: true,
                          });
                        }}
                      >
                        <Button danger icon={<DeleteOutlined />}>
                          Delete
                        </Button>
                      </Popconfirm>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};
